/**
 * English: Device agent card with Ops Score and link to detail.
 * Thai: การ์ดเอเจนต์อุปกรณ์ — Ops Score + ลิงก์รายละเอียด
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import StatusBadge from '../shared/StatusBadge';
import type { DailySummaryItem } from '../../hooks/useMemorySummary';
import { filterSummariesForDevice } from './memoryDeviceRollups';
import type { AgentRow } from './useAgentRows';
import {
  detailLinkFor,
  deviceStatusVariant,
  displayDeviceType,
} from './agentRowView';
import AgentCompetencyDashboard from './AgentCompetencyDashboard';

interface AgentManagementCardProps {
  row: AgentRow;
  orgName?: string;
  stationId?: string;
  stationDailyItems: DailySummaryItem[];
  memoryLoading?: boolean;
}

export default function AgentManagementCard({
  row,
  orgName = '',
  stationId = '',
  stationDailyItems,
  memoryLoading = false,
}: AgentManagementCardProps) {
  const { t } = useTranslation();
  const { agent, device } = row;
  const color = typeof agent.color === 'string' ? agent.color : '#64748b';
  const dtype = displayDeviceType(row);
  const topic = device?.topic?.trim() || '';
  const status = device?.device_status;
  const dm = device?.device_manage;
  const kpiShort = device?.kpi_score != null ? String(device.kpi_score) : null;
  const gradeShort = device?.grade != null ? String(device.grade) : null;

  const dailyItems = useMemo(
    () => filterSummariesForDevice(stationDailyItems, row.deviceId, agent.id),
    [stationDailyItems, row.deviceId, agent.id],
  );

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-app-border/80 bg-app-surface shadow-sm transition-shadow hover:shadow-md hover:border-app-accent/25">
      <Link
        to={detailLinkFor(row, orgName, stationId)}
        className="block px-4 pt-4 pb-3 hover:bg-app-muted/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-app-accent/40 focus-visible:ring-inset"
      >
        <div className="flex items-start gap-2.5 min-w-0">
          <span
            className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-app-border/80"
            style={{ backgroundColor: color }}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 gap-y-1">
              <h3 className="truncate text-sm font-semibold text-ink">
                {agent.name || row.deviceId}
              </h3>
              <span className="shrink-0 rounded-md bg-primary/12 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-primary">
                {dtype}
              </span>
            </div>
            <p className="mt-0.5 truncate font-mono text-[10px] text-app-text-dim" title={agent.id}>
              {row.deviceId}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {status ? (
                <StatusBadge variant={deviceStatusVariant(status)} dot className="!text-[10px] !py-0">
                  {status}
                </StatusBadge>
              ) : null}
              {(kpiShort || gradeShort) && (
                <span className="rounded-md bg-app-muted/50 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-app-text">
                  KPI {kpiShort ?? '—'}
                  {gradeShort ? ` · ${gradeShort}` : ''}
                </span>
              )}
            </div>
            {topic ? (
              <p className="mt-1.5 truncate font-mono text-[10px] text-app-text-dim" title={topic}>
                {topic}
              </p>
            ) : null}
            {dm?.device_manage_name ? (
              <p className="mt-1 line-clamp-1 text-[10px] text-app-text-dim">
                CMMS: {String(dm.device_manage_name)}
              </p>
            ) : null}
          </div>
        </div>
      </Link>

      <div className="border-t border-app-border/50 bg-gradient-to-b from-app-muted/25 to-transparent px-3 py-3">
        <AgentCompetencyDashboard
          agentId={agent.id}
          dailyItems={dailyItems}
          kpiScore={device?.kpi_score ?? null}
          loading={memoryLoading}
          compact
        />
      </div>

      <Link
        to={detailLinkFor(row, orgName, stationId)}
        className="flex items-center justify-between gap-2 border-t border-app-border/40 px-4 py-2.5 text-xs font-medium text-app-accent transition-colors hover:bg-app-accent/5 group-hover:bg-app-accent/8"
      >
        <span>{t('agents.cardViewDetail', { defaultValue: 'ดูกราฟและความจำ' })}</span>
        <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
      </Link>
    </article>
  );
}
